/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Celebration as CelebrationIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  fetchHolidays,
  deleteExistingHoliday,
} from "store/features/company/leaveManagement/holiday/holidayActions";
import {
  selectHolidays,
  selectHolidayLoading,
} from "store/features/company/leaveManagement/holiday/holidaySelectors";
import AddHolidayModal from "./AddHolidayModal";
import EditHolidayModal from "./EditHolidayModal";
import holidaysSvg from "assets/folders/holidays.svg";

const Holiday = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const holidays = useSelector(selectHolidays);
  const loading = useSelector(selectHolidayLoading);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  const getYearFromDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const availableYears = [
    ...new Set(
      holidays.map((h) => getYearFromDate(h.holiday_date)).filter((y) => y),
    ),
  ].sort((a, b) => b - a);

  const filteredHolidays = holidays.filter((holiday) => {
    const matchesSearch =
      searchTerm === "" ||
      holiday.holiday_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holiday.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesYear =
      yearFilter === "all" ||
      getYearFromDate(holiday.holiday_date) === parseInt(yearFilter);

    return matchesSearch && matchesYear;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const HolidayTypeBadge = ({ type }) => {
    if (type === "public") {
      return (
        <Chip
          icon={<CelebrationIcon sx={{ fontSize: 14 }} />}
          label="Public Holiday"
          size="small"
          sx={{
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "24px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(16,185,129,0.25)",
              color: "#6ee7b7",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<EventIcon sx={{ fontSize: 14 }} />}
        label="Company Holiday"
        size="small"
        sx={{
          backgroundColor: "#dbeafe",
          color: "#1e40af",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(59,130,246,0.25)",
            color: "#93c5fd",
          }),
        }}
      />
    );
  };

  const handleDeleteClick = (holiday) => {
    setHolidayToDelete(holiday);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (holidayToDelete) {
      setDeleting(true);
      const result = await dispatch(deleteExistingHoliday(holidayToDelete.id));
      if (result.success) {
        dispatch(fetchHolidays());
        setDeleteDialogOpen(false);
        setHolidayToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setHolidayToDelete(null);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={holidaysSvg}
              alt="Holidays"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Holidays
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage company holidays and celebrations
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Holiday
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by holiday name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant={yearFilter === "all" ? "contained" : "outlined"}
            onClick={() => setYearFilter("all")}
            sx={{
              textTransform: "capitalize",
              borderRadius: "8px",
              px: 2,
              py: 0.7,
              fontSize: "0.875rem",
              fontWeight: 500,
              ...(yearFilter === "all" && {
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                },
              }),
              ...(yearFilter !== "all" && {
                borderColor:
                  theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                "&:hover": {
                  borderColor: "#f59e0b",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }),
            }}
          >
            All Years
          </Button>
          {availableYears.map((year) => (
            <Button
              key={year}
              variant={
                yearFilter === year.toString() ? "contained" : "outlined"
              }
              onClick={() => setYearFilter(year.toString())}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(yearFilter === year.toString() && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(yearFilter !== year.toString() && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {year}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "#f59e0b",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading holidays...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Holiday Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Created At
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHolidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CelebrationIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        }}
                      >
                        No holidays found. Click "Add Holiday" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHolidays.map((holiday, index) => (
                    <TableRow
                      key={holiday.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CelebrationIcon sx={{ color: "#f59e0b" }} />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#111827",
                              }}
                            >
                              {holiday.holiday_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#6b7280"
                                    : "#9ca3af",
                              }}
                            >
                              ID: {holiday.id} | Tenant: {holiday.tenant_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EventIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {formatDate(holiday.holiday_date)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <HolidayTypeBadge type={holiday.holiday_type} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DescriptionIcon
                            sx={{ fontSize: 14, color: "#9ca3af" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {holiday.description || "-"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <EventIcon
                              sx={{ fontSize: 14, color: "#f59e0b" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              {formatDateTime(holiday.created_at)}
                            </Typography>
                          </Stack>
                          {holiday.updated_at &&
                            holiday.updated_at !== holiday.created_at && (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ opacity: 0.6 }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#9ca3af"
                                        : "#6b7280",
                                    ml: 3.5,
                                  }}
                                >
                                  <b>Updated: </b>{" "}
                                  {formatDateTime(holiday.updated_at)}
                                </Typography>
                              </Stack>
                            )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            onClick={() => {
                              setSelectedHoliday(holiday);
                              setShowEditModal(true);
                            }}
                            size="small"
                            sx={{
                              color: "#f59e0b",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(245,158,11,0.2)"
                                    : "#fffbeb",
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(holiday)}
                            size="small"
                            sx={{
                              color: "#ef4444",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(239,68,68,0.2)"
                                    : "#fef2f2",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Holiday Count Summary */}
      {!loading && holidays.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Holidays: {holidays.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredHolidays.length} of {holidays.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredHolidays.length === 0 && holidays.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              mb: 2,
            }}
          >
            No holidays match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setYearFilter("all");
            }}
            sx={{
              color: "#f59e0b",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Modals */}
      <AddHolidayModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchHolidays())}
      />

      <EditHolidayModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedHoliday(null);
        }}
        holiday={selectedHoliday}
        onSuccess={() => dispatch(fetchHolidays())}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Yellow Banner */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleDeleteCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon */}
            <Box
              component="img"
              src="/src/assets/modals/delete-modal.svg"
              alt="Delete"
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                margin: "0 auto",
                display: "block",
                mb: 2,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                textAlign: "center",
                mb: 2,
              }}
            >
              Delete Holiday
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to delete
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "#f59e0b",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{holidayToDelete?.holiday_name}"
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              This holiday will be deleted permanently
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default Holiday;
